import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import { Button } from '@/components/ui/button';
import type { SessionListItem } from '@/types';
import { MessageSquare, Trash2, Plus, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

interface SessionListProps {
  currentSessionId?: string;
  tempUserId?: string;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: () => void;
}

export default function SessionList({
  currentSessionId,
  tempUserId,
  onSessionSelect,
  onNewSession,
}: SessionListProps) {
  const queryClient = useQueryClient();
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

  // Fetch sessions
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sessions', tempUserId],
    queryFn: () => apiService.getUserSessions(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Delete session mutation
  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => apiService.deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      setDeletingSessionId(null);
    },
    onError: (error) => {
      console.error('Failed to delete session:', error);
      setDeletingSessionId(null);
    },
  });

  const handleDelete = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (window.confirm('确定要删除这个会话吗？')) {
      setDeletingSessionId(sessionId);
      deleteMutation.mutate(sessionId);
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: zhCN,
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex h-full flex-col bg-muted/30">
      {/* Header */}
      <div className="border-b bg-background p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">会话历史</h2>
          <Button
            size="sm"
            variant="outline"
            onClick={onNewSession}
            className="h-8 w-8 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto">
        {/*
          エラーをここで出す。以前はコンポーネントごと差し替えていたため、
          一覧の取得に失敗するとヘッダごと消え、**新しい会話を始める
          ボタンまで無くなっていた**。履歴が読めないことと、
          新しく話し始められることは別の話。
        */}
        {error ? (
          <div className="flex h-full items-center justify-center p-4 text-center text-sm">
            <div>
              <p className="text-destructive">履歴を読み込めませんでした</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => refetch()}
              >
                再読み込み
              </Button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : data && data.sessions.length > 0 ? (
          <div className="space-y-1 p-2">
            {data.sessions.map((session: SessionListItem) => (
              <div
                key={session.sessionId}
                onClick={() => onSessionSelect(session.sessionId)}
                className={`group relative cursor-pointer rounded-lg border p-3 transition-colors hover:bg-muted ${
                  session.sessionId === currentSessionId
                    ? 'border-primary bg-muted'
                    : 'border-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium truncate">
                        {session.title || `会话 ${session.sessionId.slice(0, 8)}`}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{session.messageCount} 条消息</span>
                      <span>·</span>
                      <span>{formatTime(session.lastMessageAt)}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => handleDelete(e, session.sessionId)}
                    disabled={deletingSessionId === session.sessionId}
                    className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"
                  >
                    {deletingSessionId === session.sessionId ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
            <div>
              <MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>暂无会话历史</p>
              <p className="mt-1 text-xs">开始新的对话吧</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
